(() => {
  if (customElements.get('growthlab-proof-carousel')) return;
  customElements.define('growthlab-proof-carousel', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.track = this.querySelector('[data-proof-track]');
      if (!this.track) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true';
      this.slides = this.dataset.autoplayMode === 'slides';
      this.hovering = false;
      this.paused = false;
      this.interacting = false;
      this.visible = false;
      this.toggle = this.querySelector('[data-proof-toggle]');
      this.track.querySelectorAll('[data-proof-clone]').forEach(item => item.remove());
      if (this.autoplay && !this.slides) {
        // Repeat existing assets for a seamless loop, never extra announced clients.
        const originals = [...this.track.children];
        originals.forEach(item => {
          const clone = item.cloneNode(true);
          clone.setAttribute('data-proof-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('inert', '');
          clone.removeAttribute('data-shopify-editor-block');
          clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
          this.track.append(clone);
        });
        this.firstClone = this.track.children[originals.length];
      }
      const hold = () => {
        clearTimeout(this.resumeTimer);
        this.interacting = true;
        this.sync();
      };
      const release = () => {
        if (!this.interacting) return;
        clearTimeout(this.resumeTimer);
        this.resumeTimer = setTimeout(() => { this.interacting = false; this.sync(); }, 4000);
      };
      this.querySelector('[data-proof-prev]')?.addEventListener('click', () => { hold(); this.move(-1); release(); }, { signal });
      this.querySelector('[data-proof-next]')?.addEventListener('click', () => { hold(); this.move(1); release(); }, { signal });
      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => { this.paused = !this.paused; this.sync(); }, { signal });
      }
      this.addEventListener('focusin', () => this.sync(), { signal });
      this.addEventListener('focusout', () => requestAnimationFrame(() => this.sync()), { signal });
      this.track.addEventListener('pointerdown', hold, { passive: true, signal });
      if (this.slides) {
        this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { this.hovering = true; this.sync(); } }, { signal });
        this.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse') { this.hovering = false; this.sync(); } }, { signal });
      }
      window.addEventListener('pointerup', release, { passive: true, signal });
      window.addEventListener('pointercancel', release, { passive: true, signal });
      this.track.addEventListener('wheel', () => { hold(); release(); }, { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault(); hold();
          if (event.key === 'Home' || event.key === 'End') {
            const end = this.loopWidth() || this.track.scrollWidth;
            this.track.scrollTo({ left: event.key === 'Home' ? 0 : end - this.track.clientWidth, behavior: 'instant' });
          } else this.move(event.key === 'ArrowLeft' ? -1 : 1);
          release();
        }
      }, { signal });
      this.motion.addEventListener('change', () => this.sync(), { signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.sync(); });
      this.observer.observe(this);
      this.sync();
    }
    loopWidth() {
      return this.firstClone ? this.firstClone.offsetLeft - this.track.firstElementChild.offsetLeft : 0;
    }
    move(direction) {
      const items = this.track.children;
      const distance = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : this.track.clientWidth;
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      this.track.scrollTo({ left: Math.max(0, Math.min(maximum, this.track.scrollLeft + direction * distance)), behavior: this.motion.matches ? 'instant' : 'smooth' });
    }
    sync() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.slideTimer);
      if (this.toggle) {
        const label = this.paused ? this.dataset.play : this.dataset.pause;
        this.toggle.setAttribute('aria-label', label);
        this.toggle.textContent = label;
      }
      if (!this.autoplay || this.paused || this.interacting || this.hovering || !this.visible || this.motion.matches || document.hidden || this.querySelector(':focus-visible')) return;
      if (this.slides) {
        if (this.track.scrollWidth <= this.track.clientWidth) return;
        this.slideTimer = setTimeout(() => {
          const maximum = this.track.scrollWidth - this.track.clientWidth;
          if (this.track.scrollLeft >= maximum - 2) this.track.scrollTo({ left: 0, behavior: 'instant' });
          else this.move(1);
          this.sync();
        }, 5000);
        return;
      }
      let previous;
      let position = this.track.scrollLeft;
      const tick = now => {
        if (previous !== undefined) {
          // Continuous 28 px/sec; preserve fractions on browsers that round scrollLeft.
          position += Math.min(now - previous, 50) * .028;
          const loop = this.loopWidth();
          if (loop && position >= loop) position %= loop;
          this.track.scrollLeft = position;
        }
        previous = now;
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.resumeTimer);
      clearTimeout(this.slideTimer);
      this.observer?.disconnect();
      this.abort?.abort();
      this.firstClone = null;
    }
  });
})();
